import { forwardRef, type ReactNode, type Ref, useImperativeHandle, useRef, useState, Children } from "react";

import { IconClickable } from "../icon/IconClickable";
import { IconType } from "../icon/IconType";
import { IconSize } from "../icon/IconSize";
import { useIsMinDesktopSize } from "../../hooks/useMediaQuery";

import * as S from "./Carousel.styled";
import { ReactSwipe, type ReactSwipeRef } from "./ReactSwipe";
import { type BoxProps } from "~/common/mui";

export interface CarouselProps extends BoxProps {
  /**
   * Objects to render
   */
  children: ReactNode;
  /**
   * Where should the controls be placed.
   * Below - below like in classic carousels
   * Inside - inside the image as required by https://agile.deloitte.de/browse/SCHAEFF-15792
   */
  controlsVariant?: "below" | "inside";

  continuous?: boolean;
}

export interface CarouselRefAttributes {
  getActivePage: () => number;
  getPageAmount: () => number;
  setActivePage: (index: number) => void;
  setNextPage: () => void;
  setPreviousPage: () => void;
}

function Cmp(props: CarouselProps, ref: Ref<CarouselRefAttributes>) {
  const swipeRef = useRef<ReactSwipeRef>(null);

  const isDesktop = useIsMinDesktopSize();

  const { children, controlsVariant = "below", continuous = false, ...rest } = props;

  const pages = Children.toArray(children);

  const [internalActivePage, setInternalInternalActivePage] = useState(0);

  const isFirstPage = internalActivePage === 0;
  const isLastPage = internalActivePage === pages.length - 1;

  const setPreviousPage = () => swipeRef.current?.prev();
  const setNextPage = () => swipeRef.current?.next();
  const setActivePage = (index: number) => swipeRef.current?.slide(index, 500);

  useImperativeHandle(ref, () => ({
    getActivePage: () => swipeRef.current?.getPos() ?? 0,
    getPageAmount: () => pages.length,
    setActivePage,
    setNextPage,
    setPreviousPage,
  }));

  const [swipeOptions] = useState(() => ({
    callback: setInternalInternalActivePage,
    continuous,
    speed: 500,
  }));

  const isInsideVariant = controlsVariant === "inside";
  const ControlsContainer = isInsideVariant ? S.ControlsInside : S.ControlsClassic;
  const PaginationContainer = isInsideVariant ? S.PaginationInsideWrapper : S.Pagination;
  const LeftArrowWrapper = isInsideVariant ? S.InsideLeftArrow : S.LeftArrow;
  const RightArrowWrapper = isInsideVariant ? S.InsideRightArrow : S.RightArrow;

  return (
    <S.Root {...rest}>
      <ReactSwipe childCount={pages.length} ref={swipeRef} className="carousel" swipeOptions={swipeOptions}>
        {(style) =>
          pages.map((slide, index) => (
            <div key={`slide-${index}`} className="carousel-row" style={style}>
              {slide}
            </div>
          ))
        }
      </ReactSwipe>
      {pages.length > 1 && (
        <ControlsContainer>
          {isDesktop && (
            <LeftArrowWrapper>
              <IconClickable
                type={IconType.ChevronLeft}
                size={IconSize.FONT_16}
                disabled={isFirstPage && !continuous}
                onIconClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setPreviousPage();
                }}
              />
            </LeftArrowWrapper>
          )}
          <PaginationContainer>
            {pages.map((_, index) => (
              <S.PaginationItem
                key={`carousel-control-${index}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setActivePage(index);
                }}
                active={internalActivePage === index}
                variant={isInsideVariant ? "inside" : "default"}
              />
            ))}
          </PaginationContainer>
          {isDesktop && (
            <RightArrowWrapper>
              <IconClickable
                type={IconType.ChevronRight}
                size={IconSize.FONT_16}
                disabled={isLastPage && !continuous}
                onIconClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setNextPage();
                }}
              />
            </RightArrowWrapper>
          )}
        </ControlsContainer>
      )}
    </S.Root>
  );
}

export const Carousel2 = forwardRef(Cmp);
